/**
 * Token persistence.
 *
 * The portal is entered from an emailed link, and the director may close the
 * tab mid-signing and come back to it. The session therefore has to survive a
 * reload, which rules out memory-only storage.
 *
 * The cost is real and worth stating plainly: anything in localStorage is
 * readable by any script that gets injected into the page, so an XSS becomes a
 * session theft. The mitigation is a short-lived access token plus the refresh
 * rotation in `baseQuery.ts` — not the storage layer. If the API ever grows
 * httpOnly cookie sessions, this module is the only thing that needs deleting.
 */

const ACCESS_TOKEN_KEY = 'accessToken';
const REFRESH_TOKEN_KEY = 'refreshToken';
const USER_ID_KEY = 'userId';
const ASIC_COMPANY_ID_KEY = 'asicCompanyId';
const SESSION_VERIFIED_KEY = 'sessionVerified';

export interface StoredTokens {
  accessToken: string;
  refreshToken: string;
  /** The director's ASIC member id — the subject of every director endpoint. */
  userId: string;
  asicCompanyId?: string;
}

/** Safari in private mode throws on any localStorage access rather than
 *  returning null, which would otherwise take down the whole app at boot. */
const read = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const write = (key: string, value: string): void => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Storage is unavailable; the session lasts for this page view only.
  }
};

const remove = (key: string): void => {
  try {
    localStorage.removeItem(key);
  } catch {
    // Nothing to clear if storage was never writable.
  }
};

export const setTokens = (tokens: StoredTokens): void => {
  write(ACCESS_TOKEN_KEY, tokens.accessToken);
  write(REFRESH_TOKEN_KEY, tokens.refreshToken);
  write(USER_ID_KEY, tokens.userId);
  if (tokens.asicCompanyId) {
    write(ASIC_COMPANY_ID_KEY, tokens.asicCompanyId);
  }
};

/**
 * Marks the session as having cleared every gate, OTP included.
 *
 * Kept separate from the tokens because `/director/login` stores those BEFORE
 * OTP is verified. Seeding the auth slice from token presence alone therefore
 * let a reload on the OTP screen come back fully authenticated, walking past
 * the second factor entirely.
 */
export const markSessionVerified = (): void => {
  write(SESSION_VERIFIED_KEY, 'true');
};

export const isSessionVerified = (): boolean => read(SESSION_VERIFIED_KEY) === 'true';

export const getAccessToken = (): string | null => read(ACCESS_TOKEN_KEY);
export const getRefreshToken = (): string | null => read(REFRESH_TOKEN_KEY);
export const getUserId = (): string | null => read(USER_ID_KEY);
export const getAsicCompanyId = (): string | null => read(ASIC_COMPANY_ID_KEY);

/** Clears every key, not just the tokens. waive-dp left `userId` and
 *  `asicCompanyId` behind on logout, which let a stale id leak into the next
 *  director's session on a shared device. */
export const clearTokens = (): void => {
  remove(ACCESS_TOKEN_KEY);
  remove(REFRESH_TOKEN_KEY);
  remove(USER_ID_KEY);
  remove(ASIC_COMPANY_ID_KEY);
  remove(SESSION_VERIFIED_KEY);
};
